import { useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Badge,
    Box,
    ClickAwayListener,
    Paper,
    Popper,
    Tooltip,
    useMediaQuery,
    List,
    MenuItem,
    Avatar,
    Grid,
    Stack,
} from '@mui/material';
import MainCard from 'components/ui/Card/MainCard';
import IconButton from 'components/ui/Button/IconButton';
import Transitions from 'components/common/Transitions/Transitions';
import { ThemeMode } from 'types/config';
import { useGetDagRunsBatchCustomQuery } from 'store/airflow/enhancedAirflowApi';
import { Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { LuWorkflow } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { workflowExecutionDetailsPath } from 'routes/paths';
import { formatDateTime } from 'utils/date';
import { trimDagIdPrefix } from 'utils/workflow';
import { useGetProjectData } from 'hooks/useGetProjectData';

const WorkflowExecutionNotifications = () => {
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const anchorRef = useRef<any>(null);
    const [open, setOpen] = useState(false);
    const [limit, setLimit] = useState(5);

    const { data: projects } = useGetProjectData({});

    const { data: dagRunsData, isFetching: dagRunsIsFetching } = useGetDagRunsBatchCustomQuery(
        {
            listDagRunsForm: {
                page_limit: limit,
                order_by: '-execution_date',
            },
        },
        {
            pollingInterval: 20000,
        }
    );

    const dagRuns = dagRunsData?.dag_runs;

    const projectShortNames = projects?.map(p => p?.ShortName).filter((v): v is string => !!v) ?? [];

    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleShowMore = () => {
        setLimit(prev => prev + 10);
    };

    const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';
    const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }} id='workflow-execution-notifications'>
            <Tooltip title='Workflow Executions'>
                <IconButton
                    color='secondary'
                    variant='light'
                    sx={{
                        color: 'text.primary',
                        bgcolor: open ? iconBackColorOpen : iconBackColor,
                        borderRadius: '20px',
                    }}
                    aria-label='open profile'
                    ref={anchorRef}
                    aria-controls={open ? 'profile-grow' : undefined}
                    aria-haspopup='true'
                    onClick={handleToggle}>
                    <Badge color='primary'>
                        <LuWorkflow size={20} />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? -5 : 0, 9],
                            },
                        },
                    ],
                }}>
                {({ TransitionProps }) => (
                    <Transitions
                        type='grow'
                        position={matchesXs ? 'top' : 'top-right'}
                        sx={{ overflow: 'hidden' }}
                        in={open}
                        {...TransitionProps}>
                        <Paper
                            sx={{
                                boxShadow: theme.customShadows.z1,
                                width: 420,
                                borderRadius: 2,
                                overflow: 'hidden',
                                [theme.breakpoints.down('md')]: {
                                    width: 320,
                                },
                            }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    title='Workflow Executions'
                                    elevation={0}
                                    border={false}
                                    content={false}
                                    sx={{
                                        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'grey.900' : 'grey.50',
                                    }}>
                                    <List
                                        component='nav'
                                        sx={{
                                            p: 0,
                                            maxHeight: 400,
                                            overflow: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                bgcolor: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                bgcolor:
                                                    theme.palette.mode === ThemeMode.DARK ? 'grey.700' : 'grey.300',
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    bgcolor:
                                                        theme.palette.mode === ThemeMode.DARK ? 'grey.600' : 'grey.400',
                                                },
                                            },
                                        }}>
                                        {dagRuns?.map(dagRun => (
                                            <MenuItem
                                                key={`${dagRun?.dag_id}-${dagRun?.dag_run_id}`}
                                                onClick={() =>
                                                    navigate(
                                                        workflowExecutionDetailsPath(
                                                            dagRun?.dag_id,
                                                            dagRun?.dag_run_id as string
                                                        )
                                                    )
                                                }
                                                sx={{
                                                    mx: 1,
                                                    my: 0.5,
                                                    px: 2,
                                                    py: 1.5,
                                                    borderRadius: 1.5,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        bgcolor: theme.palette.primary.lighter,
                                                        transform: 'translateX(4px)',
                                                    },
                                                }}>
                                                <Grid container alignItems='center' spacing={2}>
                                                    <Grid item>
                                                        <Tooltip title={`${dagRun?.state}`} placement='left'>
                                                            <Avatar
                                                                sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    ...((dagRun?.state as string) === 'success'
                                                                        ? {
                                                                              color: 'success.main',
                                                                              bgcolor: 'success.lighter',
                                                                          }
                                                                        : (dagRun?.state as string) === 'running'
                                                                        ? {
                                                                              color: 'primary.main',
                                                                              bgcolor: 'primary.lighter',
                                                                          }
                                                                        : (dagRun?.state as string) === 'failed'
                                                                        ? {
                                                                              color: 'error.main',
                                                                              bgcolor: 'error.lighter',
                                                                          }
                                                                        : {}),
                                                                }}
                                                                variant='rounded'>
                                                                <LuWorkflow />
                                                            </Avatar>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs zeroMinWidth>
                                                        <Stack spacing={0.5}>
                                                            <Stack
                                                                direction='row'
                                                                justifyContent='space-between'
                                                                alignItems='center'>
                                                                <Typography variant='subtitle2' fontWeight={600} noWrap>
                                                                    {dagRun?.dag_run_id}
                                                                </Typography>
                                                                <Typography
                                                                    variant='caption'
                                                                    color='text.secondary'
                                                                    sx={{ minWidth: '40%', textAlign: 'right' }}>
                                                                    {formatDateTime(dagRun?.data_interval_start)}
                                                                </Typography>
                                                            </Stack>
                                                            <Typography
                                                                variant='caption'
                                                                color='text.secondary'
                                                                noWrap
                                                                sx={{ fontSize: '0.7rem' }}>
                                                                {trimDagIdPrefix(dagRun?.dag_id, projectShortNames)}
                                                            </Typography>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </MenuItem>
                                        ))}
                                        {(!dagRuns || dagRuns.length === 0) && (
                                            <Box
                                                sx={{
                                                    textAlign: 'center',
                                                    py: 6,
                                                    px: 3,
                                                }}>
                                                <Typography variant='body2' color='text.secondary'>
                                                    No Workflow Executions
                                                </Typography>
                                            </Box>
                                        )}
                                    </List>
                                    <Box
                                        sx={{
                                            px: 2.5,
                                            py: 1.5,
                                            bgcolor: 'background.paper',
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                        }}>
                                        <LoadingButton
                                            variant='outlined'
                                            color='primary'
                                            loading={dagRunsIsFetching}
                                            disabled={dagRunsData ? dagRunsData.total_entries! <= limit : true}
                                            fullWidth
                                            onClick={handleShowMore}
                                            sx={{
                                                borderRadius: 1.5,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: theme.customShadows.z1,
                                                },
                                            }}>
                                            Show more
                                        </LoadingButton>
                                    </Box>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default WorkflowExecutionNotifications;
